import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import translate from "../../../i18n/translate";
import "./style.css";
import { getSellers } from "../../../actions/sellerCreditsAction";
import Select from "react-select";
import { intl } from "../../../i18n/provider";
import AddCreditModal from "../../modals/AddCreditModal/AddCreditModal";
import { Box, CircularProgress, Grid, Typography, Fab, Tooltip } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { END_POINTS } from "shared/endPoints/endPoints";
import { getAllSellersApiCaller } from "shared/endPoints/apiCallers";
import { formatSellerOptionsForSelect } from "shared/utils/formatUtils";
import { isArray } from "lodash";
import { USER_TYPES_WITH_ADMIN } from "shared/constants/constants";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
const SellerCredits = ({ isDarktheme, type }) => {
  const [seller, setSeller] = useState(null);
  const [sellerType, setSellerType] = useState({ value: "", label: "Type" });
  const [openAddCredit, setOpenAddCredit] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc"); // or 'desc'
  const selectedResellerId = selectedReseller?.value || "";
  const history = useHistory();
  const location = useLocation();
  const firstRowRef = useRef(null);
  const lastRowRef = useRef(null);
  const resellerOptionsRef = useRef([]);
  const urlSellerIdProcessed = useRef(false);
  const { data, isLoading } = useQuery(
    [END_POINTS.getAllSellers.key, selectedResellerId || 0],
    () => getAllSellersApiCaller({ resellerId: selectedResellerId })
  );

  const { all_sellers, ...rest } = data?.data || {};
  const allSellersTotals = rest;
  const sellers = all_sellers;

  const [filteredData, setFilteredData] = useState(sellers);

  const SortArrow = useCallback(({ field }) => {
    if (sortField !== field) return <KeyboardArrowDownIcon />;
    return sortDirection === "asc" ? (
      <KeyboardArrowUpIcon />
    ) : (
      <KeyboardArrowDownIcon />
    );
  }, [sortField, sortDirection]);

  const handleSort = useCallback((field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  }, [sortField, sortDirection]);

  const sortedData = useMemo(() => {
    if (!sortField || !filteredData) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (!isNaN(aValue) && !isNaN(bValue)) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDirection]);

  const filtered = useMemo(() => {
    if (!isArray(sellers)) return resellerOptionsRef.current;

    // Only update reseller options when we have the full list (no reseller selected)
    if (!selectedResellerId) {
      const filteredResellers = sellers.filter(
        (x) =>
          x.type === USER_TYPES_WITH_ADMIN.admin.value ||
          x.type === USER_TYPES_WITH_ADMIN.reseller.value
      );
      const formattedOptions = formatSellerOptionsForSelect(filteredResellers, true);
      resellerOptionsRef.current = formattedOptions;
      return formattedOptions;
    }

    // Keep the existing reseller options when filtered by specific reseller
    return resellerOptionsRef.current;
  }, [sellers, selectedResellerId]);

  useEffect(() => {
    document.title = "Seller Credits | PhonePlay ";
  }, []);

  useEffect(() => {
    if (sellers?.length) {
      setFilteredData(sellers);
    }
  }, [sellers]);

  // Handle seller_id from URL query parameter (e.g., from report page click)
  useEffect(() => {
    if (urlSellerIdProcessed.current || !sellers?.length) return;

    const params = new URLSearchParams(location.search);
    const sellerIdFromUrl = params.get('seller_id');

    if (sellerIdFromUrl) {
      const foundSeller = sellers.find(s => s.seller_id === sellerIdFromUrl);
      if (foundSeller) {
        const sellerOption = {
          value: foundSeller.seller_id,
          label: `${foundSeller.name} ${foundSeller.seller_id}`,
          balance: foundSeller.balance,
        };
        setSeller(sellerOption);
        setOpenAddCredit(true);
        urlSellerIdProcessed.current = true;
        // Clean up URL to avoid re-triggering
        history.replace('/add_credits');
      }
    }
  }, [sellers, location.search, history]);

  const options = useMemo(() => {
    if (!filteredData) return [];
    return filteredData.map((item) => ({
      value: item.seller_id,
      label: `${item.name} ${item.seller_id}`,
      balance: item.balance,
    }));
  }, [filteredData]);

  const renderTotals = useMemo(() => (
    <>
      <Grid item md={4} xs={12}>
        <Typography>
          {translate("total_credit")} :{" "}
          <b>{allSellersTotals?.total_credits}</b>
        </Typography>
      </Grid>
      <Grid item md={4} xs={12}>
        <Typography>
          {translate("total_debt")} : <b>{allSellersTotals?.total_debt}</b>
        </Typography>
      </Grid>
      <Grid item md={4} xs={12}>
        <Typography>
          {translate("total_points")} :{" "}
          <b>{allSellersTotals?.total_points}</b>
        </Typography>
      </Grid>
    </>
  ), [allSellersTotals]);

  const sellerOptions = useMemo(() => [
    { value: "", label: "All" },
    { value: "admin", label: "Admin" },
    { value: "seller", label: "Seller" },
  ], []);

  const filterData = useCallback((e) => {
    const { value } = e.target;

    if (!sellers) return;

    setFilteredData(
      sellers.filter((seller) => {
        const typeMatches = !sellerType.value ||
          seller.type?.toLowerCase() === sellerType.value?.toLowerCase();

        const searchMatches = !value || value.trim() === '' ||
          (seller.name?.toLowerCase().includes(value.toLowerCase()) ||
            seller.seller_id?.toLowerCase().includes(value.toLowerCase()) ||
            seller.mobile_number?.toLowerCase().includes(value.toLowerCase()));

        return typeMatches && searchMatches;
      })
    );
  }, [sellers, sellerType.value]);

  const typeFilter = useCallback((value) => {
    setSellerType(value);

    if (!value || !value.value || !sellers) {
      setFilteredData(sellers);
      return;
    }

    setFilteredData(
      sellers.filter((seller) =>
        seller.type?.toLowerCase() === value.value.toLowerCase()
      )
    );
  }, [sellers]);

  const handleIdClick = useCallback((sellerId) => {
    const clickedSeller = options.find((seller) => seller.value === sellerId);
    if (clickedSeller) {
      setSeller(clickedSeller);
      setOpenAddCredit(true);
    }
  }, [options]);

  const toggleAddCredit = useCallback((state = false) => {
    setOpenAddCredit(state);
  }, []);

  const scrollToFirstRecord = useCallback(() => {
    if (firstRowRef.current) {
      firstRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Optional: highlight the first row briefly
      firstRowRef.current.style.backgroundColor = isDarktheme ? '#4a4a4a' : '#e3f2fd';
      setTimeout(() => {
        if (firstRowRef.current) {
          firstRowRef.current.style.backgroundColor = '';
        }
      }, 1500);
    }
  }, [isDarktheme]);

  const scrollToLastRecord = useCallback(() => {
    if (lastRowRef.current) {
      lastRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      lastRowRef.current.style.backgroundColor = isDarktheme ? '#4a4a4a' : '#e3f2fd';
      setTimeout(() => {
        if (lastRowRef.current) {
          lastRowRef.current.style.backgroundColor = '';
        }
      }, 1500);
    }
  }, [isDarktheme]);

  return (
    <>
      <AddCreditModal
        seller={seller}
        open={openAddCredit}
        onClose={() => toggleAddCredit()}
      />
      <div className="credits">
        <div className="card card-home">
          <div>
            <h1
              className={
                !isDarktheme ? "header-text p-4" : "dark-header-text p-4"
              }
            >
              {type === "debt"
                ? translate("Seller Debt")
                : translate("add credits")}
            </h1>
          </div>
        </div>

        <Grid container spacing={1}>
          <Grid item md={3} xs={12}>
            <Select
              placeholder={translate("type")}
              options={sellerOptions}
              onChange={typeFilter}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Select
              name="reseller_id"
              placeholder={intl.formatMessage({
                id: "select_reseller",
              })}
              label={intl.formatMessage({ id: "reseller" })}
              options={filtered}
              value={selectedReseller}
              onChange={(val) => setSelectedReseller(val)}
              isClearable
            />
          </Grid>
          <Grid item md={5} xs={12}>
            <input
              className="form-control"
              placeholder={`${intl.formatMessage({ id: "search_record" })}`}
              onChange={filterData}
            />
          </Grid>
          <Grid
            alignItems={"center"}
            height={"40px"}
            item
            container
            md={12}
            xs={12}
          >
            {renderTotals}
          </Grid>
        </Grid>
        <div
          className="table-wrapper"
          style={{
            padding: 0,
          }}
        >
          {isLoading ? (
            <Grid container justifyContent={"center"} alignItems="center" sx={{ py: 5 }}>
              {/* iOS-style spinner */}
              <Box
                sx={{
                  position: "relative",
                  width: "50px",
                  height: "50px",
                }}
              >
                {[...Array(12)].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: "3px",
                      height: "12px",
                      marginLeft: "-1.5px",
                      marginTop: "-6px",
                      borderRadius: "1.5px",
                      backgroundColor: "rgba(25, 118, 210, 0.9)",
                      transformOrigin: "center center",
                      transform: `rotate(${index * 30}deg) translate(0, -17px)`,
                      animation: "ios-fade 1.2s linear infinite",
                      animationDelay: `${-1.2 + index * 0.1}s`,
                      "@keyframes ios-fade": {
                        "0%": { opacity: 1 },
                        "100%": { opacity: 0.2 },
                      },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          ) : (
            <table
              className={
                !isDarktheme
                  ? "table table-striped"
                  : "table dark-table-striped"
              }
            >
              <thead>
                <tr className="first-Row">
                  <td
                    onClick={() => handleSort("seller_id")}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {translate("user id")}
                    <SortArrow field="seller_id" />
                  </td>
                  <td>{translate("name")}</td>
                  <td>{translate("Mobile No.")}</td>
                  <td
                    onClick={() => handleSort("balance")}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {translate("Balance")} <SortArrow field="balance" />
                  </td>
                  <td
                    onClick={() => handleSort("debth")}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {translate("Debt")} <SortArrow field="debth" />
                  </td>
                  <td>{translate("city")}</td>
                  <td>{translate("country")}</td>
                  <td>{translate("enabled")}</td>
                  <td
                    onClick={() => handleSort("points")}
                    style={{ cursor: "pointer" }}
                  >
                    {translate("points")} <SortArrow field="points" />
                  </td>
                  <td>{translate("type")}</td>
                </tr>
              </thead>
              <tbody>
                {sortedData?.map((seller, index) => {
                  const isInactive = seller.enabled !== "true";

                  return (
                    <tr
                      key={index.toString()}
                      ref={index === 0 ? firstRowRef : (index === sortedData.length - 1 ? lastRowRef : null)}
                      className={isInactive ? 'inactive-seller-row' : ''}
                    >
                      <td
                        onClick={() => {
                          handleIdClick(seller.seller_id);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {seller.seller_id}
                      </td>
                      <td
                        onClick={() => {
                          history.push({
                            pathname: '/Profile',
                            state: {
                              seller_id: seller.seller_id,
                              seller_name: seller.name
                            }
                          });
                        }}
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {seller.name}
                      </td>
                      <td>{seller.mobile_number}</td>
                      <td>{seller.balance} ₪</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          history.push(
                            `/seller_debt?seller_id=${seller?.seller_id}`
                          );
                        }}
                      >
                        {seller.debth}
                      </td>
                      <td style={{ textTransform: "capitalize" }}>
                        {seller.city}
                      </td>
                      <td style={{ textTransform: "capitalize" }}>
                        {seller.country}
                      </td>
                      <td>
                        {seller.enabled === "true"
                          ? translate("Yes")
                          : translate("No")}
                      </td>
                      <td>{seller.points}</td>
                      <td>
                        {seller.type === "N/A" || !seller?.type?.trim()?.length
                          ? translate("Not available")
                          : translate(seller.type)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Floating Action Buttons: First and Last record */}
      {sortedData && sortedData.length > 0 && (
        <>
          <Tooltip title={translate("Go to first record")} placement="left">
            <Fab
              color="primary"
              aria-label="first record"
              onClick={scrollToFirstRecord}
              sx={{
                position: 'fixed',
                bottom: 88,
                right: 24,
                zIndex: 1000,
              }}
            >
              <FirstPageIcon />
            </Fab>
          </Tooltip>
          <Tooltip title={translate("Go to last record")} placement="left">
            <Fab
              color="secondary"
              aria-label="last record"
              onClick={scrollToLastRecord}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <LastPageIcon />
            </Fab>
          </Tooltip>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  sellers: state.credits.sellers,
  isDarktheme: state.darkTheme.isDarktheme,
});

export default connect(mapStateToProps, {
  getSellers,
})(SellerCredits);
